import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  EOtpremnicaDropdownDto,
  EOtpremniceClient,
  RacuniClient,
  TipDokumentaOtpremnica,
  TipRacuna,
  VrstaRacuna,
} from '@kodit/core/data-api';
import { AlertService, LocationService } from '@kodit/core/services';
import { DynamicDialogConfig, DynamicDialogRef } from '@kodit/core/shared-ui';
import { FormArray, FormControl } from '@angular/forms';
import { ConfirmationService } from 'primeng/api';
import { Subscription } from 'rxjs';

interface OtpremnicaZaPreuzimanje extends EOtpremnicaDropdownDto {
  jePrijemnica: boolean;
}

@Component({
  selector: 'kodit-add-eotpremnica-form',
  templateUrl: './add-eotpremnica-form.component.html',
  styleUrls: ['./add-eotpremnica-form.component.scss'],
})
export class AddEOtpremnicaFormComponent implements OnInit, OnDestroy {
  private _subs: Subscription = new Subscription();

  tipOtpremnica = TipDokumentaOtpremnica.OTPREMNICA;
  tipPrijemnica = TipDokumentaOtpremnica.PRIJEMNICA;

  eOtpremnicaArray = new FormArray([]);
  ePrijemnicaArray = new FormArray([]);

  racunId: number;
  racunTip: TipRacuna;
  racunVrsta: VrstaRacuna;

  constructor(
    private _dialogRef: DynamicDialogRef,
    private _alertService: AlertService,
    private _racuniClient: RacuniClient,
    private _eOtpremniceClient: EOtpremniceClient,
    private _confirmationService: ConfirmationService,
    private _locationService: LocationService,
    public dialogConfig: DynamicDialogConfig
  ) {}

  ngOnInit(): void {
    this.racunId = this.dialogConfig.data.racunId;
    this.racunTip = this.dialogConfig.data.tip;
    this.racunVrsta = this.dialogConfig.data.vrsta;

    this._subs.add(
      this._dialogRef.onSubmit.subscribe(() => {
        this._save();
      })
    );
  }

  selectedOtpremnice(items: EOtpremnicaDropdownDto[]) {
    this.eOtpremnicaArray.clear();
    items.forEach((x) => this.eOtpremnicaArray.push(new FormControl(x)));
  }

  selectedPrijemnice(items: EOtpremnicaDropdownDto[]) {
    this.ePrijemnicaArray.clear();
    items.forEach((x) => this.ePrijemnicaArray.push(new FormControl(x)));
  }

  private _save() {
    const otpremnice: OtpremnicaZaPreuzimanje[] = [
      ...this.eOtpremnicaArray.value.map((x: EOtpremnicaDropdownDto) => ({
        ...x,
        jePrijemnica: false,
      })),
      ...this.ePrijemnicaArray.value.map((x: EOtpremnicaDropdownDto) => ({
        ...x,
        jePrijemnica: true,
      })),
    ];

    if (!otpremnice || otpremnice.length === 0) {
      this._alertService.addWarnMsg('Morate odabrati bar jednu eOtpremnicu ili ePrijemnicu');
      return;
    }

    this._subs.add(
      this._racuniClient
        .addEOtpremnicaToRacun({
          racunId: this.racunId,
          eOtpremnicaBaseIds: otpremnice.map((x) => x.eOtpremnicaId),
        } as any)
        .subscribe((res) => {
          if (res.succeeded) {
            this._alertService.addSuccessMsg(res.messages[0]);

            if (this.racunTip === TipRacuna.DOKUMENT_O_SMANJENJU) {
              this._pitajZaPreuzimanjeStavki(otpremnice);
            } else {
              this._dialogRef.close(true);
            }
          } else {
            this._alertService.addWarnMsg(res.messages[0]);
          }
        })
    );
  }

  /*
   * Za dos, pitamo korisnika za svaku povezanu eOtpremnicu (ne i ePrijemnicu tj. preuzimanje stavki
   * je dostupno samo sa otpremnice trenutno, a na sefu moze i za prijemnice...?) da li zeli da preuzme njene stavke. 
   * Ako je bar jedno DA, otvaramo formu za izmenu dokumenta sa preuzetim stavkama vec dodatim, da bi korisnik pregledao i sam sacuvao
   */
  private async _pitajZaPreuzimanjeStavki(otpremnice: OtpremnicaZaPreuzimanje[]) {
    const idsZaPreuzimanje: number[] = [];
    const eOtpremnice = otpremnice.filter((x) => !x.jePrijemnica);

    for (const otpremnica of eOtpremnice) {
      const zeliPreuzimanje = await this._potvrdiPreuzimanje(otpremnica);
      if (zeliPreuzimanje) {
        idsZaPreuzimanje.push(otpremnica.eOtpremnicaId);
      }
    }

    this._dialogRef.close(idsZaPreuzimanje.length === 0);

    if (idsZaPreuzimanje.length > 0) {
      this._locationService.routeToRacunEdit(this.racunId, this.racunTip, this.racunVrsta, {
        eOtpremnicaBaseIdsZaPreuzimanje: idsZaPreuzimanje,
      });
    }
  }

  private _potvrdiPreuzimanje(otpremnica: OtpremnicaZaPreuzimanje): Promise<boolean> {
    return new Promise((resolve) => {
      this._confirmationService.confirm({
        message: `Da li želite da preuzmete stavke otpremnice ${otpremnica.broj}?`,
        header: 'Preuzimanje stavki',
        acceptLabel: 'Da',
        rejectLabel: 'Ne',
        icon: 'pi pi-question-circle',
        accept: () => resolve(true),
        reject: () => resolve(false),
      });
    });
  }

  ngOnDestroy(): void {
    this._subs.unsubscribe();
  }
}
